import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";

export type PingResult = {
  ok: boolean;
  uid: string;
  email: string | null;
  claims: { tenantId: string | null; role: string | null };
  serverTime: string;
};

export type BootstrapResult = { ok: true; role: "platform_admin" };

export type CreateTenantInput = {
  slug: string;
  name: string;
  theme?: { primary?: string };
  legal?: { legalName?: string; taxId?: string; taxIdFormat?: string };
  initialAdminEmail?: string;
};
export type CreateTenantResult = {
  ok: true;
  tenantId: string;
  adminAssigned: { uid: string; email: string } | null;
};

export type JoinTenantInput = { tenantId: string };
export type JoinTenantResult = { ok: true; tenantId: string; alreadyMember: boolean };

export type RequestPasswordResetInput = { email: string };
export type RequestPasswordResetResult = { ok: true };

export type InviteTenantAdminInput = {
  tenantId: string;
  email: string;
  role?: "tenant_admin" | "ambassador";
};
export type InviteTenantAdminResult = { ok: true; token: string; expiresAt: string };

export type AcceptInviteInput = { tenantId: string; token: string };
export type AcceptInviteResult = { ok: true; tenantId: string; role: "tenant_admin" | "ambassador" };

export type CreateTenantSelfServeInput = {
  country: "CL" | "AR";
  taxIdFormat: "CL-RUT" | "AR-CUIT" | "generic";
  taxId: string;
  personType: "natural" | "juridica";
  businessType: string;
  name: string;
  legalName?: string;
  address: string;
  city?: string;
  website?: string;
  instagram?: string;
  locations: "1" | "2-5" | "6+";
  ownerRole?: string;
  ownerName?: string;
  phone?: string;
  refSource?: string;
};
export type CreateTenantSelfServeResult = {
  ok: true;
  tenantId: string;
  verificationStatus: string;
  autoVerified: boolean;
};

export type CreatePurchaseInput = { storagePath: string; discountCode?: string };
export type CreatePurchaseResult = {
  ok: true;
  purchaseId: string;
  ticketsCount: number;
  status: "approved" | "pending_admin_review" | "rejected";
  amount: number | null;
  productDetected: boolean;
  ticketEmitted: boolean;
};

export type RedeemTicketsResult = { ok: true; rewardId: string; ticketsUsed: number };

export type UpdateTenantOcrConfigInput = {
  tenantId: string;
  config: {
    identifiers?: string[];
    taxIdHints?: string[];
    knownProducts?: string[];
    validPrices?: number[];
    acceptAnyAmount?: boolean;
    confidenceThreshold?: number;
  };
};
export type UpdateTenantOcrConfigResult = { ok: true };

export type SubmitAmbassadorApplicationInput = {
  name: string;
  instagram: string;
  followers?: string;
  message?: string;
};
export type SubmitAmbassadorApplicationResult = { ok: true; applicationId: string };

export type ReviewAmbassadorApplicationInput = {
  applicationId: string;
  decision: "approve" | "reject";
  reason?: string;
};
export type ReviewAmbassadorApplicationResult =
  | { ok: true; decision: "approved"; ambassadorId: string; code: string }
  | { ok: true; decision: "rejected" };

export type ActivateDiscountVoucherInput = { code: string };
export type ActivateDiscountVoucherResult = {
  ok: true;
  voucherId: string;
  code: string;
  discountPercent: number;
  expiresAt: string;
  alreadyActive: boolean;
};

export type GenerateAmbassadorCodeInput = { discountPercent?: number };
export type GenerateAmbassadorCodeResult = { ok: true; code: string; discountPercent: number };

export type ToggleDiscountCodeInput = { code: string; active: boolean };
export type ToggleDiscountCodeResult = { ok: true; code: string; active: boolean };

export type AnalyzeSampleReceiptInput = { storagePath: string };
export type AnalyzeSampleReceiptResult = {
  ok: true;
  ocrText: string;
  detectedRuts: string[];
  detectedTaxIds: string[];
  detectedAmounts: number[];
  detectedLegalNames: string[];
  detectedProducts: string[];
  detectedAddresses: string[];
};

export type UpdateTenantLoyaltyConfigInput = {
  tenantId: string;
  config: { ticketsForReward?: number; rewardLabel?: string; rewardItem?: string; rewardIcon?: string };
};
export type UpdateTenantLoyaltyConfigResult = { ok: true };

export type UpdateTenantAmbassadorConfigInput = {
  tenantId: string;
  config: {
    defaultDiscountPercent?: number;
    defaultCommissionRate?: number;
    voucherTtlMinutes?: number;
    postUseBlockHours?: number;
    commissionCapMonthlyClp?: number | null;
  };
};
export type UpdateTenantAmbassadorConfigResult = { ok: true };

export type UpdateTenantCategoriesInput = { tenantId: string; categories: string[] };
export type UpdateTenantCategoriesResult = { ok: true; categories: string[] };

export type RegisterPushTokenInput = { token: string; platform: "web" | "ios" | "android" };
export type RegisterPushTokenResult = { ok: true };

export type GeofenceLocation = {
  name: string;
  lat: number;
  lng: number;
  radiusMeters: number;
  address?: string;
};
export type UpdateTenantGeofenceInput = { tenantId: string; locations: GeofenceLocation[] };
export type UpdateTenantGeofenceResult = { ok: true; count: number };

export type ImpersonateCustomerInput = { uid: string };
export type ImpersonateCustomerResult = {
  ok: true;
  customToken: string;
  targetEmail: string | null;
  targetDisplayName: string | null;
  targetTenantId: string | null;
};

export type ReviewTenantVerificationInput = {
  tenantId: string;
  decision: "verified" | "rejected";
  note?: string;
};
export type ReviewTenantVerificationResult = { ok: true; decision: "verified" | "rejected" };

export type UpdateTenantBrandingInput = {
  tenantId: string;
  name?: string;
  themePrimary?: string;
  instagram?: string;
  website?: string;
};
export type UpdateTenantBrandingResult = { ok: true };

export type SetTenantLogoInput = { tenantId: string; storagePath: string | null };
export type SetTenantLogoResult = { ok: true; logoUrl: string | null };

export type UpdateMercadoPagoConfigInput = {
  tenantId: string;
  accessToken?: string | null;
  webhookSecret?: string | null;
  publicKey?: string | null;
  legalRut?: string;
  fantasyName?: string;
  sandbox?: boolean;
};
export type UpdateMercadoPagoConfigResult = { ok: true; enabled: boolean };

export type CreatePaymentPreferenceInput = {
  tenantId?: string;
  amount: number;
  description: string;
  currency?: string;
  payer?: { email?: string; name?: string; surname?: string; rut?: string };
  metadata?: Record<string, unknown>;
};
export type CreatePaymentPreferenceResult = {
  ok: true;
  paymentId: string;
  initPoint: string;
  preferenceId: string;
};

export type SoftDeleteTenantInput = { tenantId: string; confirmSlug: string };
export type SoftDeleteTenantResult = { ok: true; alreadyDeleted: boolean };

export type RestoreTenantInput = { tenantId: string };
export type RestoreTenantResult = { ok: true };

export type InvitePlatformAdminInput = { email: string; note?: string };
export type InvitePlatformAdminResult = { ok: true; token: string; expiresAt: string };

export type AcceptPlatformInviteInput = { token: string };
export type AcceptPlatformInviteResult = { ok: true };

export type RevokePlatformAdminInput = { uid: string };
export type RevokePlatformAdminResult = { ok: true };

export type ListPlatformAdminsResult = {
  ok: true;
  admins: Array<{
    uid: string;
    email: string | null;
    displayName: string | null;
    lastSignIn: string | null;
    isSelf: boolean;
  }>;
};

export type GetCustomerQrTokenResult = {
  ok: true;
  token: string;
  expiresInMinutes: number;
  voucher: {
    id: string;
    code: string;
    discountPercent: number;
    expiresAt: string;
  };
};

export type CashierScanCustomerQrInput = { token: string };
export type CashierScanCustomerQrResult = {
  ok: true;
  customer: {
    uid: string;
    displayName: string | null;
    email: string | null;
    phone: string | null;
    visits: number;
    pendingTickets: number;
  };
  voucher: {
    id: string;
    code: string;
    discountPercent: number;
    commissionRate: number;
    ambassadorId: string;
    expiresAt: string;
  } | null;
  ambassadorName: string | null;
};

export type CashierConfirmPurchaseInput = {
  token: string;
  amountGross: number;
  category?: string | null;
  note?: string | null;
};
export type CashierConfirmPurchaseResult = {
  ok: true;
  purchaseId: string;
  amountGross: number;
  amountDiscount: number;
  amountFinal: number;
  netPostDiscount: number;
  commissionAmount: number;
  voucherApplied: { code: string; discountPercent: number } | null;
};

export type CashierUndoPurchaseInput = { purchaseId: string };
export type CashierUndoPurchaseResult = { ok: true };

export type CashierRequestCorrectionInput = { purchaseId: string; reason: string };
export type CashierRequestCorrectionResult = { ok: true };

export type CashierLookupCustomerInput = { query: string };
export type CashierLookupCustomerResult = {
  ok: true;
  matches: Array<{
    uid: string;
    displayName: string | null;
    email: string | null;
    phone: string | null;
  }>;
};

export type InviteStaffInput = { tenantId: string; email: string; displayName?: string };
export type InviteStaffResult = {
  ok: true;
  token: string;
  expiresAt: string;
  acceptUrl: string;
  whatsappUrl: string;
};

export type AcceptStaffInviteInput = { tenantId: string; token: string };
export type AcceptStaffInviteResult = { ok: true; tenantId: string; role: "staff" };

export type UpdateCustomerProfileInput = { displayName?: string; phone?: string };
export type UpdateCustomerProfileResult = { ok: true };

export type RevokeStaffInput = { tenantId: string; uid: string };
export type RevokeStaffResult = { ok: true };

export type AdminEditPurchaseInput = {
  tenantId: string;
  purchaseId: string;
  amountFinal?: number;
  status?: "approved" | "rejected" | "undone";
  reason: string;
};
export type AdminEditPurchaseResult = { ok: true };

export type CreateSubscriptionInput = {
  tenantId?: string;
  amount?: number;
  reason?: string;
  payerEmail?: string;
  frequency?: number;
  frequencyType?: "days" | "months";
};
export type CreateSubscriptionResult = {
  ok: true;
  subscriptionId: string;
  initPoint: string;
  preapprovalId: string;
};

const wrap = <Req, Res>(name: string) => httpsCallable<Req, Res>(functions, name);

export const api = {
  ping: wrap<void, PingResult>("ping"),
  bootstrapPlatformAdmin: wrap<void, BootstrapResult>("bootstrapPlatformAdmin"),
  createTenant: wrap<CreateTenantInput, CreateTenantResult>("createTenant"),
  joinTenant: wrap<JoinTenantInput, JoinTenantResult>("joinTenant"),
  requestPasswordReset: wrap<RequestPasswordResetInput, RequestPasswordResetResult>(
    "requestPasswordReset"
  ),
  inviteTenantAdmin: wrap<InviteTenantAdminInput, InviteTenantAdminResult>("inviteTenantAdmin"),
  acceptInvite: wrap<AcceptInviteInput, AcceptInviteResult>("acceptInvite"),
  createTenantSelfServe: wrap<CreateTenantSelfServeInput, CreateTenantSelfServeResult>(
    "createTenantSelfServe"
  ),
  createPurchase: wrap<CreatePurchaseInput, CreatePurchaseResult>("createPurchase"),
  redeemTickets: wrap<void, RedeemTicketsResult>("redeemTickets"),
  updateTenantOcrConfig: wrap<UpdateTenantOcrConfigInput, UpdateTenantOcrConfigResult>(
    "updateTenantOcrConfig"
  ),
  submitAmbassadorApplication: wrap<SubmitAmbassadorApplicationInput, SubmitAmbassadorApplicationResult>(
    "submitAmbassadorApplication"
  ),
  reviewAmbassadorApplication: wrap<ReviewAmbassadorApplicationInput, ReviewAmbassadorApplicationResult>(
    "reviewAmbassadorApplication"
  ),
  activateDiscountVoucher: wrap<ActivateDiscountVoucherInput, ActivateDiscountVoucherResult>(
    "activateDiscountVoucher"
  ),
  generateAmbassadorCode: wrap<GenerateAmbassadorCodeInput, GenerateAmbassadorCodeResult>(
    "generateAmbassadorCode"
  ),
  toggleDiscountCode: wrap<ToggleDiscountCodeInput, ToggleDiscountCodeResult>("toggleDiscountCode"),
  analyzeSampleReceipt: wrap<AnalyzeSampleReceiptInput, AnalyzeSampleReceiptResult>(
    "analyzeSampleReceipt"
  ),
  updateTenantLoyaltyConfig: wrap<UpdateTenantLoyaltyConfigInput, UpdateTenantLoyaltyConfigResult>(
    "updateTenantLoyaltyConfig"
  ),
  updateTenantBranding: wrap<UpdateTenantBrandingInput, UpdateTenantBrandingResult>(
    "updateTenantBranding"
  ),
  setTenantLogo: wrap<SetTenantLogoInput, SetTenantLogoResult>("setTenantLogo"),
  updateMercadoPagoConfig: wrap<UpdateMercadoPagoConfigInput, UpdateMercadoPagoConfigResult>(
    "updateMercadoPagoConfig"
  ),
  createPaymentPreference: wrap<CreatePaymentPreferenceInput, CreatePaymentPreferenceResult>(
    "createPaymentPreference"
  ),
  createSubscription: wrap<CreateSubscriptionInput, CreateSubscriptionResult>("createSubscription"),
  softDeleteTenant: wrap<SoftDeleteTenantInput, SoftDeleteTenantResult>("softDeleteTenant"),
  restoreTenant: wrap<RestoreTenantInput, RestoreTenantResult>("restoreTenant"),
  invitePlatformAdmin: wrap<InvitePlatformAdminInput, InvitePlatformAdminResult>("invitePlatformAdmin"),
  acceptPlatformInvite: wrap<AcceptPlatformInviteInput, AcceptPlatformInviteResult>(
    "acceptPlatformInvite"
  ),
  revokePlatformAdmin: wrap<RevokePlatformAdminInput, RevokePlatformAdminResult>("revokePlatformAdmin"),
  listPlatformAdmins: wrap<void, ListPlatformAdminsResult>("listPlatformAdmins"),
  getCustomerQrToken: wrap<void, GetCustomerQrTokenResult>("getCustomerQrToken"),
  cashierScanCustomerQr: wrap<CashierScanCustomerQrInput, CashierScanCustomerQrResult>(
    "cashierScanCustomerQr"
  ),
  cashierConfirmPurchase: wrap<CashierConfirmPurchaseInput, CashierConfirmPurchaseResult>(
    "cashierConfirmPurchase"
  ),
  cashierUndoPurchase: wrap<CashierUndoPurchaseInput, CashierUndoPurchaseResult>(
    "cashierUndoPurchase"
  ),
  cashierRequestCorrection: wrap<CashierRequestCorrectionInput, CashierRequestCorrectionResult>(
    "cashierRequestCorrection"
  ),
  cashierLookupCustomer: wrap<CashierLookupCustomerInput, CashierLookupCustomerResult>(
    "cashierLookupCustomer"
  ),
  inviteStaff: wrap<InviteStaffInput, InviteStaffResult>("inviteStaff"),
  acceptStaffInvite: wrap<AcceptStaffInviteInput, AcceptStaffInviteResult>("acceptStaffInvite"),
  updateCustomerProfile: wrap<UpdateCustomerProfileInput, UpdateCustomerProfileResult>(
    "updateCustomerProfile"
  ),
  revokeStaff: wrap<RevokeStaffInput, RevokeStaffResult>("revokeStaff"),
  adminEditPurchase: wrap<AdminEditPurchaseInput, AdminEditPurchaseResult>("adminEditPurchase"),
  updateTenantAmbassadorConfig: wrap<UpdateTenantAmbassadorConfigInput, UpdateTenantAmbassadorConfigResult>(
    "updateTenantAmbassadorConfig"
  ),
  updateTenantCategories: wrap<UpdateTenantCategoriesInput, UpdateTenantCategoriesResult>(
    "updateTenantCategories"
  ),
  registerPushToken: wrap<RegisterPushTokenInput, RegisterPushTokenResult>("registerPushToken"),
  updateTenantGeofence: wrap<UpdateTenantGeofenceInput, UpdateTenantGeofenceResult>(
    "updateTenantGeofence"
  ),
  impersonateCustomer: wrap<ImpersonateCustomerInput, ImpersonateCustomerResult>("impersonateCustomer"),
  reviewTenantVerification: wrap<ReviewTenantVerificationInput, ReviewTenantVerificationResult>(
    "reviewTenantVerification"
  )
};

export function callableErrorMessage(err: unknown): string {
  const e = err as { code?: string; message?: string };
  if (e?.message) return e.message;
  return "No pudimos completar la acción.";
}
